// ============================================================================
// VOTING OVERVIEW TAB — WorkItemDetailModal overview for voting sessions
// (item_type='voting'). The voting session IS the work item (shared UUID since
// the work-items migration). The overview shows session meta, ballot-governance
// config and lifecycle actions (launch / open / close / delete); the agenda,
// participants, quorum and results live in their own modal tabs.
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/digital-voting/agenda-and-voter-roll.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/digital-voting/agenda-and-voter-roll)
 */

import React, { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ExternalLink, Gavel, Loader2, Play, Square, Trash2, Video,
} from 'lucide-react';
import type { UnifiedOverviewTabProps } from '@/components/work-items/UnifiedOverviewTab';
import {
  closeSession,
  deleteSession,
  launchSession,
  openSession,
  type VotingLaunchResult,
} from '@/app/client-zone/modules/total-operations/components/voting/votingApi';
import {
  Badge,
  SESSION_TYPE_LABELS,
  StatusBadge,
  formatDateTime,
  formatWindow,
} from '@/app/client-zone/modules/total-operations/components/voting/shared';
import { notify } from '@/lib/notifications';
import { authService } from '@/lib/authService';
import UserDocsLink from '@/components/shared/UserDocsLink';
import { useVotingSessionDetail } from './useVotingSessionDetail';

interface Props extends UnifiedOverviewTabProps {
  /** Refresh the underlying work item in the modal (status/date mirrors
   *  change when the session lifecycle moves). */
  onRefresh?: () => void;
}

function CopyLink({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard?.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
      title={url}
    >
      {copied ? 'Copied!' : label}
    </button>
  );
}

export default function VotingOverviewTab({ workItem, isDark = false, onRefresh }: Props) {
  const router = useRouter();
  const sessionId = String(workItem.id);
  const { detail, loading, error, reload } = useVotingSessionDetail(sessionId);
  const [busy, setBusy] = useState(false);
  const [launchResult, setLaunchResult] = useState<VotingLaunchResult | null>(null);

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      try {
        await fn();
        await reload();
        onRefresh?.();
      } catch (err) {
        notify.error(err instanceof Error ? err.message : 'Action failed');
      } finally {
        setBusy(false);
      }
    },
    [onRefresh, reload],
  );

  if (loading && !detail) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
        <Gavel className={`h-10 w-10 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
        <p className="text-sm text-red-600">{error || 'Session not found'}</p>
      </div>
    );
  }

  const { session, quorum, items, voter_count: voterCount, can_manage: canManage } = detail;
  const isLive = session.status === 'live';

  // The signed-in user's governance role on this session (chairperson /
  // secretary / scrutineer / member / observer from work_item_assignees).
  // Server-side it grants chairperson+secretary manage rights; here it only
  // drives the label/badge so observers see "Join meeting", not "Join as Host".
  const currentUserId = authService.getUser()?.id;
  const myOfficialRole =
    workItem.assignees?.find(
      (a) => a.user_id != null && Number(a.user_id) === Number(currentUserId),
    )?.role ?? null;
  // Launch drives a meeting session into the room; for a meeting already live
  // via its pre-voting window, launch just starts/links the meeting itself.
  const canLaunch =
    canManage &&
    (['draft', 'scheduled'].includes(session.status) ||
      (session.delivery_mode === 'meeting' && isLive && !session.meeting_id));
  // Any session with a voting window (async, or a meeting with pre-voting) can
  // have its window opened early while scheduled.
  const hasWindow = session.delivery_mode === 'async' || session.voting_opens_at != null;
  const canOpenWindow = canManage && session.status === 'scheduled' && hasWindow;
  const canClose = canManage && ['live', 'paused'].includes(session.status);
  const canDelete = canManage && ['draft', 'cancelled'].includes(session.status);
  const launchFromPreVote = isLive && session.delivery_mode === 'meeting' && !session.meeting_id;

  const handleLaunch = () =>
    act(async () => {
      const result = await launchSession(sessionId);
      setLaunchResult(result);
      if (result.host_join_url) {
        if (result.join_url) {
          try {
            await navigator.clipboard.writeText(result.join_url);
          } catch {
            // Clipboard unavailable — the link stays visible below.
          }
        }
        notify.success('Meeting launched — guest invite copied, joining as host…');
        router.push(result.host_join_url);
      } else if (result.join_url) {
        notify.success('Meeting launched — invitation link ready to share');
      } else if (result.scheduled) {
        notify.success('Pre-voting scheduled — opens at the window start');
      } else {
        notify.success('Pre-voting is now open');
      }
    });

  const settings = session.settings ?? {};
  const configRows: Array<{ label: string; value: string }> = [
    {
      label: 'Delivery',
      value:
        session.delivery_mode === 'async'
          ? 'Pre-voting (asynchronous)'
          : session.voting_opens_at
            ? 'Live meeting with pre-voting'
            : 'Live meeting',
    },
    { label: 'Quorum', value: session.quorum_required ? `Required · ${session.quorum_percent ?? '—'}%` : 'Not required' },
    { label: 'Ballot', value: settings.vote_mode === 'secret' || settings.anonymity === 'anonymous' ? 'Secret / anonymous' : 'Identified' },
    { label: 'Vote weighting', value: settings.weighted ? 'Weighted (per voter)' : 'One vote each' },
    { label: 'Vote changes', value: settings.change_vote_allowed ? 'Allowed until close' : 'Final once cast' },
    { label: 'Roll', value: settings.roll_required === false ? 'Open (no roll check)' : 'Closed roll only' },
    { label: 'Majority', value: settings.majority_rule === 'two_thirds' ? 'Two-thirds' : settings.majority_rule === 'custom' ? `Custom (${settings.custom_threshold_pct ?? '—'}%)` : 'Simple majority' },
    { label: 'Results visibility', value: settings.results_visibility === 'host_only' ? 'Host only' : settings.results_visibility === 'after_close' ? 'After close' : 'Everyone' },
  ];

  return (
    <div className="space-y-4 p-4">
      {/* Session summary + lifecycle actions */}
      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={session.status} />
              <Badge
                label={
                  session.delivery_mode === 'async'
                    ? 'Pre-voting'
                    : session.voting_opens_at
                      ? 'Meeting + pre-vote'
                      : 'Live meeting'
                }
                color="#6d28d9"
                bg="#ede9fe"
              />
              {isLive && <Badge label="Auto-refreshing" color="#166534" bg="#dcfce7" />}
              {myOfficialRole && (
                <Badge
                  label={`Official · ${myOfficialRole}`}
                  color="#1e40af"
                  bg="#dbeafe"
                />
              )}
              <UserDocsLink docsPath="/total-operations/workflows/digital-voting/agenda-and-voter-roll#the-overview-tab--status-actions-and-links" label="User guide: Agenda and voter roll" />
            </div>
            <p className={`mt-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {session.item_number && <span className="font-mono">{session.item_number} · </span>}
              {SESSION_TYPE_LABELS[session.session_type] ?? session.session_type} · {formatWindow(session)}
              {session.location_name && <> · {session.location_name}</>}
              {' '}· {items.length} agenda item{items.length === 1 ? '' : 's'} · {voterCount} voter{voterCount === 1 ? '' : 's'}
              {quorum && quorum.eligible > 0 && (
                <> · Roll {quorum.present}/{quorum.eligible}{quorum.met != null && (quorum.met ? ' · quorum met' : ' · below quorum')}</>
              )}
            </p>
            {session.status === 'closed' && session.closed_at && (
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Closed {formatDateTime(session.closed_at)}</p>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {canLaunch && (
              <button
                onClick={handleLaunch}
                disabled={busy || items.length === 0}
                title={items.length === 0 ? 'Add agenda items first' : undefined}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <Play className="h-4 w-4" /> {launchFromPreVote ? 'Start meeting' : 'Launch'}
              </button>
            )}
            {canOpenWindow && (
              <button
                onClick={() =>
                  act(async () => {
                    await openSession(sessionId);
                    notify.success('Voting window opened');
                  })
                }
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <Play className="h-4 w-4" /> {session.delivery_mode === 'meeting' ? 'Open pre-voting' : 'Open Voting'}
              </button>
            )}
            {isLive && session.delivery_mode === 'meeting' && session.meeting_id && (
              <a
                href={`/call/room?callId=${session.meeting_id}&type=video&isMeetingRoom=true&vote_session=${session.id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Video className="h-4 w-4" /> {canManage ? 'Join as Host' : 'Join meeting'} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {canClose && (
              <button
                onClick={() =>
                  act(async () => {
                    await closeSession(sessionId);
                    notify.success('Session closed — final tallies frozen');
                  })
                }
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <Square className="h-4 w-4" /> Close Session
              </button>
            )}
            {canDelete && (
              <button
                onClick={() =>
                  act(async () => {
                    await deleteSession(sessionId);
                    notify.success('Draft session deleted');
                  })
                }
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            )}
          </div>
        </div>

        {/* Launch result / share links */}
        {(launchResult || (isLive && hasWindow)) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-blue-50 px-3 py-2">
            <span className="text-xs font-medium text-blue-800">Share:</span>
            {launchResult?.join_url && <CopyLink label="Guest invite link" url={launchResult.join_url} />}
            {launchResult?.host_join_url && <CopyLink label="Host join link" url={launchResult.host_join_url} />}
            {(launchResult?.display_url ?? detail.projector_url) && isLive && (
              <CopyLink
                label="Projector / results screen"
                url={launchResult?.display_url ?? detail.projector_url!}
              />
            )}
            {hasWindow && (
              <span className="text-xs text-blue-700">
                Voter links are issued per member on the Participants tab.
              </span>
            )}
          </div>
        )}
      </div>

      {/* Ballot governance config */}
      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
        <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Ballot configuration</h3>
        <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
          {configRows.map((row) => (
            <div key={row.label} className="flex items-baseline justify-between gap-3 text-sm">
              <dt className={isDark ? 'text-gray-400' : 'text-gray-500'}>{row.label}</dt>
              <dd className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{row.value}</dd>
            </div>
          ))}
        </dl>
        {session.description && (
          <p className={`mt-3 border-t pt-3 text-sm ${isDark ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-600'}`}>
            {session.description}
          </p>
        )}
      </div>
    </div>
  );
}
